import { Elements } from '@stripe/react-stripe-js';
import { Suspense, useEffect, useState } from 'react';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import { Notice } from 'hb-react/shared/components/Notice';
import { STRIPE_PAYMENT_TYPES } from 'hb-react/shared/components/Payment/constants';
import { usePaymentContext } from 'hb-react/shared/components/Payment/context/PaymentContext';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import BancontactForm from './BancontactForm';
import { useAsyncPayment } from './hooks/use-async-payment';
import IBANForm from './IBANForm';
import IdealPayment from './IdealPayment';
import { CreditCardForm } from './PaymentForm';
import { PaymentMethodSelector } from './PaymentMethodSelector';

const StripeInjector = ({ children, stripeAccount }) => {
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    if (stripe) {
      return;
    }

    import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(window.__HB_ENV__.STRIPE_PUBLISHABLE_KEY, {
        stripeAccount,
      }).then(setStripe),
    );
  }, [stripeAccount]);

  if (!stripe) {
    return <Loader size={24} />;
  }

  return <Elements stripe={stripe}>{children}</Elements>;
};

const PaymentMethod = ({ method }) => {
  if (method === STRIPE_PAYMENT_TYPES.IDEAL) {
    return <IdealPayment />;
  }

  if (method === STRIPE_PAYMENT_TYPES.BANCONTACT) {
    return <BancontactForm />;
  }

  if (method === STRIPE_PAYMENT_TYPES.CARD) {
    return <CreditCardForm />;
  }

  if (method === 'sepa_debit') {
    return <IBANForm />;
  }

  return <Notice>{__('Unsupported payment method')}</Notice>;
};

const PaymentStripe = () => {
  const { paymentAccountConfig, onSuccess, amount } = usePaymentContext();

  const [method, setMethod] = useState(STRIPE_PAYMENT_TYPES.CARD);
  const [error, setError] = useState(null);
  const { flagEvStripeDirectChargeForAllCC } = useFeatureFlag();

  useAsyncPayment({
    onSuccess,
    onError: ({ source_type: type }) => {
      setMethod(type);
      setError(true);
    },
  });

  const {
    available_payment_options: paymentOptions,
    external_account_id: stripeAccountId,
  } = paymentAccountConfig;

  const withSelector =
    paymentOptions.bancontact ||
    paymentOptions.ideal ||
    paymentOptions.sepa_debit;

  const stripeOptions =
    // Setup Intents for free trial periods require a stripe account even if it is CC.
    // 3DS challenge requiring CCs also require a stripe account, but we don't know if its 3DS at this point
    // To solve that we make another instance in react/shared/components/Payment/components/Stripe/PaymentForm/index.jsx with the stripe account.
    method !== STRIPE_PAYMENT_TYPES.CARD ||
    Math.abs(amount) === 0 ||
    flagEvStripeDirectChargeForAllCC
      ? { stripeAccount: stripeAccountId }
      : {};

  return (
    <Box flexDirection="column">
      {error && (
        <Box mb={3}>
          <AdditionalInformation id="async-payment-error" mt={0}>
            {s__('Stripe|External bank declined your payment request')}
          </AdditionalInformation>
        </Box>
      )}

      {withSelector && (
        <PaymentMethodSelector
          onSelect={setMethod}
          selectedPaymentMethod={method}
          gateway={paymentAccountConfig}
        />
      )}

      {method && (
        <StripeInjector {...stripeOptions} key={stripeOptions?.stripeAccount}>
          <Box
            pt={2}
            flexDirection="column"
            data-testid="payment-provider-container"
          >
            <Box mb={4}>{s__('Payment|Enter payment details:')}</Box>
            <PaymentMethod method={method} />
          </Box>
        </StripeInjector>
      )}
    </Box>
  );
};

const PaymentStripeWrapper = (props) => (
  <Suspense fallback={null}>
    <PaymentStripe {...props} />
  </Suspense>
);

export default PaymentStripeWrapper;
